import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { downscaleImage } from '../../utils/imageDownscale';
import { enqueueUpload, isNetworkError } from '../../utils/offlineUploadQueue';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Upload, X, Star, Loader2, CheckCircle2, Camera, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN = () => localStorage.getItem('token');

const TalentPostEvent = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [report, setReport] = useState(null);
  const [form, setForm] = useState({ report_text: '', overall_rating: 0, issues_reported: '' });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/post-event/my/${bookingId}`);
      setPhotos(res.data.photos || []);
      if (res.data.report) {
        setReport(res.data.report);
        setForm({
          report_text: res.data.report.report_text || '',
          overall_rating: res.data.report.overall_rating || 0,
          issues_reported: res.data.report.issues_reported || '',
        });
      }
    } catch (e) {
      if (e.response?.status !== 404) {
        toast.error(e.response?.data?.detail || 'Failed to load submissions');
      }
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let queued = 0;
    for (const file of files) {
      const upFile = await downscaleImage(file);
      try {
        const fd = new FormData();
        fd.append('file', upFile);
        await api.post(
          `/post-event/upload-photo?booking_id=${bookingId}`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      } catch (err) {
        if (isNetworkError(err)) {
          await enqueueUpload({
            endpoint: `/post-event/upload-photo?booking_id=${bookingId}`,
            file: upFile,
            description: 'post-event photo',
          });
          queued += 1;
        } else {
          toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
        }
      }
    }
    setUploading(false);
    if (queued > 0) {
      toast.info(`No signal — ${queued} photo${queued > 1 ? 's' : ''} saved on this device`, {
        description: 'They will upload automatically when you are back online.',
      });
    } else {
      toast.success(`Uploaded ${files.length} photo${files.length > 1 ? 's' : ''}`);
    }
    fetchData();
    e.target.value = '';
  };

  const submitReport = async () => {
    if (!form.report_text.trim() || form.report_text.trim().length < 5) {
      toast.error('Report must be at least 5 characters');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/post-event/report', {
        booking_id: bookingId,
        report_text: form.report_text.trim(),
        overall_rating: form.overall_rating || null,
        issues_reported: form.issues_reported?.trim() || null,
      });
      toast.success(report ? 'Report updated' : 'Report submitted');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const imgSrc = (url) => `${API}${url}?auth=${encodeURIComponent(TOKEN() || '')}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="talent-post-event-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold">Post-Event Report</h1>
            <p className="text-xs text-gray-500">Share photos and a quick recap of your shift</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* Photos */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Camera className="w-4 h-4 text-pink-600" />
              Event Photos ({photos.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <label
              htmlFor="post-event-photo-input"
              className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-cyan-300 bg-cyan-50 hover:bg-cyan-100 rounded-lg cursor-pointer transition"
              data-testid="post-event-upload-label"
            >
              {uploading ? (
                <Loader2 className="w-5 h-5 animate-spin text-cyan-600" />
              ) : (
                <>
                  <Upload className="w-5 h-5 text-cyan-600 mb-1" />
                  <span className="text-sm text-cyan-700 font-medium">Upload photos</span>
                  <span className="text-xs text-gray-500">JPG/PNG/WebP · up to 15MB each</span>
                </>
              )}
              <input
                id="post-event-photo-input"
                type="file"
                accept="image/*"
                multiple
                onChange={handleUpload}
                className="hidden"
                disabled={uploading}
                data-testid="post-event-photo-input"
              />
            </label>

            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2" data-testid="post-event-photo-grid">
                {photos.map((p) => (
                  <a
                    key={p.id}
                    href={imgSrc(p.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block aspect-square rounded overflow-hidden bg-gray-100 border border-gray-200"
                  >
                    <img src={imgSrc(p.url)} alt="" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Report */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Send className="w-4 h-4 text-pink-600" />
              Your Recap
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="report-text">What happened at the event?</Label>
              <Textarea
                id="report-text"
                rows={5}
                value={form.report_text}
                onChange={(e) => setForm((f) => ({ ...f, report_text: e.target.value }))}
                placeholder="Brief summary — turnout, brand engagement, memorable moments…"
                data-testid="report-text-input"
              />
              <p className="text-xs text-gray-500 mt-1">Min. 5 characters · {form.report_text.length}/5000</p>
            </div>

            <div>
              <Label>Overall rating</Label>
              <div className="flex items-center gap-1 mt-1" data-testid="report-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, overall_rating: n }))}
                    className={`p-1 rounded transition ${form.overall_rating >= n ? 'text-amber-500' : 'text-gray-300'} hover:text-amber-400`}
                    data-testid={`rating-star-${n}`}
                  >
                    <Star className={`w-6 h-6 ${form.overall_rating >= n ? 'fill-amber-400' : ''}`} />
                  </button>
                ))}
                {form.overall_rating > 0 && (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, overall_rating: 0 }))}
                    className="ml-2 text-xs text-gray-500 hover:text-red-500"
                  >
                    <X className="w-4 h-4 inline" /> Clear
                  </button>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="issues">Issues or flags (optional)</Label>
              <Textarea
                id="issues"
                rows={2}
                value={form.issues_reported}
                onChange={(e) => setForm((f) => ({ ...f, issues_reported: e.target.value }))}
                placeholder="e.g. staffing shortage, venue access problems, product mix-up…"
                data-testid="report-issues-input"
              />
            </div>

            <Button
              onClick={submitReport}
              disabled={submitting || !form.report_text.trim()}
              className="w-full bg-pink-600 hover:bg-pink-700 text-white"
              data-testid="submit-report-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : report ? (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {report ? 'Update Report' : 'Submit Report'}
            </Button>

            {report && (
              <p className="text-xs text-gray-500 text-center">
                Last submitted {new Date(report.submitted_at).toLocaleString()}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TalentPostEvent;
